@if (isOpen()) {
  <!-- Scrim: dims the page behind the drawer and closes it on a click outside. -->
  <div class="sav-ai-scrim" (click)="close()" aria-hidden="true"></div>

  <aside
    class="sav-ai-panel"
    role="dialog"
    aria-modal="true"
    aria-label="SavingArena AI assistant"
    (keydown)="onKeydown($event)"
  >
    <header class="sav-ai-panel-head">
      <span class="sav-ai-panel-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="6" y="6" width="12" height="12" rx="2" />
          <path d="M12 2V5" />
          <path d="M12 19V22" />
          <path d="M2 12H5" />
          <path d="M19 12H22" />
        </svg>
      </span>
      <div class="sav-ai-panel-title">
        <strong>SavingArena AI</strong>
        <span>Answers from our own catalogue</span>
      </div>
      <button type="button" class="sav-ai-panel-close" (click)="close()" aria-label="Close assistant">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </header>

    <div class="sav-ai-panel-scroll" #scroller>
      @if (messages().length === 0) {
        <div class="sav-ai-empty">
          <p>Ask about a brand, a product, or the kind of saving you are after.</p>
          <div class="sav-ai-chips">
            <button type="button" (click)="useSuggestion('Nike')">Nike</button>
            <button type="button" (click)="useSuggestion('Running shoes under 100')">
              Running shoes under 100
            </button>
            <button type="button" (click)="useSuggestion('Do you have any coupon for Adidas?')">
              Coupons for Adidas
            </button>
          </div>
        </div>
      }

      @for (message of messages(); track message.id) {
        @if (message.role === 'user') {
          <div class="sav-ai-turn sav-ai-turn-user">
            <p>{{ message.text }}</p>
          </div>
        } @else {
          <div class="sav-ai-turn sav-ai-turn-bot">
            @if (message.pending) {
              <div class="sav-ai-thinking" role="status" aria-live="polite">
                <span></span><span></span><span></span>
                <em>Thinking…</em>
              </div>
            } @else {
              <p class="sav-ai-reply" [class.is-failed]="message.failed">{{ message.text }}</p>

              @if (message.results; as results) {
                @if (results.stores.length) {
                  <p class="sav-ai-group">Stores</p>
                  <div class="sav-ai-cards">
                    @for (store of results.stores; track store.id) {
                      <a class="sav-ai-card sav-ai-card-store" [routerLink]="storeLink(store)" (click)="close()">
                        <!-- The name follows as text, so the mark is decorative; a store without a
                             logo shows its name on white instead of nothing. -->
                        <app-store-logo
                          [logoUrl]="store.logoImagePath ? (store.logoImagePath | assetUrl) : ''"
                          [name]="store.name"
                          decorative
                        />
                        <span class="sav-ai-card-name">{{ store.name }}</span>
                      </a>
                    }
                  </div>
                }

                @if (results.coupons.length) {
                  <p class="sav-ai-group">Coupons &amp; offers</p>
                  <div class="sav-ai-list">
                    @for (coupon of results.coupons; track coupon.id) {
                      <div class="sav-ai-row">
                        <div class="sav-ai-row-main">
                          <strong>{{ coupon.name }}</strong>
                          <span class="sav-ai-row-meta">
                            @if (coupon.store?.name) {
                              <span>{{ coupon.store?.name }}</span>
                            }
                            <!-- Read from the admin's own badge text; blank when it names none. -->
                            @if (badgeDiscount(coupon)) {
                              <span class="sav-ai-pill">{{ badgeDiscount(coupon) }}</span>
                            }
                            <!-- The teaser only. The full code is revealed on the store page, on a
                                 click that is attributed to the store — the same flow the coupon
                                 cards use everywhere else on the site. -->
                            @if (coupon.hasCode) {
                              <span class="sav-ai-code">Code ···{{ coupon.codeSuffix }}</span>
                            }
                            @if (coupon.isVerified) {
                              <span class="sav-ai-verified">Verified</span>
                            }
                          </span>
                        </div>
                        @if (couponLink(coupon); as link) {
                          <a [routerLink]="link" (click)="close()">{{
                            coupon.hasCode ? 'Show Code' : 'Get Deal'
                          }}</a>
                        }
                      </div>
                    }
                  </div>
                }

                @if (results.products.length) {
                  <p class="sav-ai-group">Products</p>
                  <div class="sav-ai-cards">
                    @for (product of results.products; track product.id) {
                      <a class="sav-ai-card" [routerLink]="productLink(product)" (click)="close()">
                        @if (product.thumbnailImagePath) {
                          <img
                            [src]="product.thumbnailImagePath | assetUrl"
                            [alt]="product.name"
                            loading="lazy"
                            (error)="hideBrokenImage($event)"
                          />
                        }
                        <span class="sav-ai-card-name">{{ product.name }}</span>
                        <!-- The stored price string, shown exactly as the feed supplies it -->
                        @if (product.newPrice) {
                          <span class="sav-ai-card-price">
                            {{ product.newPrice }}
                            <!-- 0 when the feed supplied no genuine markdown, so the badge only
                                 shows for a real one. -->
                            @if (product.discountPercent > 0) {
                              <em>-{{ product.discountPercent }}%</em>
                            }
                          </span>
                        }
                      </a>
                    }
                  </div>
                }

                @if (results.categories.length) {
                  <p class="sav-ai-group">Categories</p>
                  <div class="sav-ai-chips">
                    @for (category of results.categories; track category.id) {
                      <a [routerLink]="'/categories/' + category.slug" (click)="close()">
                        {{ category.name }}
                      </a>
                    }
                  </div>
                }
              }

              @if (message.suggestions?.length) {
                <div class="sav-ai-chips sav-ai-chips-followup">
                  @for (suggestion of message.suggestions; track suggestion) {
                    <button type="button" (click)="useSuggestion(suggestion)">{{ suggestion }}</button>
                  }
                </div>
              }
            }
          </div>
        }
      }
    </div>

    <form class="sav-ai-panel-input" (submit)="$event.preventDefault(); send()">
      <input
        #input
        type="text"
        [value]="draft()"
        (input)="onDraft($any($event.target).value)"
        (keydown)="onKeydown($event)"
        placeholder="Ask about coupons, deals, products or stores…"
        aria-label="Ask SavingArena AI"
        [disabled]="isBusy()"
      />
      <button type="submit" [disabled]="isBusy() || !draft().trim()" aria-label="Send">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 12l16-8-6 8 6 8-16-8Z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </form>
  </aside>
}

